import { supabase } from "../lib/supabase";

export class FeedbackService {
  /**
   * Get all feedback items
   * @returns {Promise<Array>} Array of feedback items with employee info and screenshots
   */
  static async getAllFeedback() {
    try {
      const { data, error } = await supabase
        .from("feedback")
        .select(`
          *,
          employees(name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error("Error fetching feedback:", error);
      throw error;
    }
  }

  /**
   * Create a new feedback item
   * @param {Object} feedbackData - The feedback data
   * @returns {Promise<Object>} The created feedback item
   */
  static async createFeedback(feedbackData) {
    try {
      // Get the current user's email
      const { data: { user } } = await supabase.auth.getUser();
      // Get the employee ID based on the user's email
      let employeeId = null;
      if (user?.email) {
        const { data: employee, error: empError } = await supabase
          .from("employees")
          .select("id")
          .eq("email", user.email)
          .single();
        
        if (empError) {
          console.warn("Could not find employee for user:", user.email, empError);
        } else {
          employeeId = employee?.id;
        }
      }
      
      const insertData = {
        ...feedbackData,
        created_by: employeeId,
        status: 'open' // Set initial status
      };
      const { data, error } = await supabase
        .from("feedback")
        .insert([insertData])
        .select(`
          *,
          employees(name)
        `)
        .single();

      if (error) {
        console.error("Supabase insert error:", error);
        throw error;
      }
      return data;
    } catch (error) {
      console.error("Error creating feedback - full error:", error);
      throw error;
    }
  }

  /**
   * Update a feedback item
   * @param {string} id - The feedback ID
   * @param {Object} updates - The updates to apply
   * @returns {Promise<Object>} The updated feedback item
   */
  static async updateFeedback(id, updates) {
    try {
      // Automatically manage timestamps based on status transitions
      if (updates.status !== undefined) {
        const oldStatus = updates._oldStatus;
        const newStatus = updates.status;

        // Set started_at when moving to 'in_progress' (if not already set)
        if (newStatus === 'in_progress' && !updates.started_at) {
          updates.started_at = new Date().toISOString();
        }

        // Set completed_at when moving to 'completed'
        if (newStatus === 'completed') {
          updates.completed_at = new Date().toISOString();
        }

        // Clear completed_at when moving FROM 'completed' to other statuses
        if (oldStatus === 'completed' && (newStatus === 'open' || newStatus === 'in_progress')) {
          updates.completed_at = null;
        }

        // Clean up helper property
        delete updates._oldStatus;
      }

      // Legacy support: Handle old completed boolean (during migration phase)
      if (updates.completed !== undefined) {
        updates.status = updates.completed ? 'completed' : 'open';
        updates.completed_at = updates.completed ? new Date().toISOString() : null;
        delete updates.completed;
      }

      const { data, error} = await supabase
        .from("feedback")
        .update(updates)
        .eq("id", id)
        .select(`
          *,
          employees(name)
        `)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error updating feedback:", error);
      throw error;
    }
  }

  /**
   * Delete a feedback item
   * @param {string} id - The feedback ID
   * @returns {Promise<void>}
   */
  static async deleteFeedback(id) {
    try {
      const { error } = await supabase
        .from("feedback")
        .delete()
        .eq("id", id);

      if (error) throw error;
    } catch (error) {
      console.error("Error deleting feedback:", error);
      throw error;
    }
  }

  /**
   * Subscribe to feedback changes
   * @param {Function} onInsert - Callback for new feedback
   * @param {Function} onUpdate - Callback for updated feedback
   * @param {Function} onDelete - Callback for deleted feedback
   * @returns {Object} Subscription object
   */
  static subscribeFeedback(onInsert, onUpdate, onDelete) {
    const subscription = supabase
      .channel("feedback_changes")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "feedback" },
        async (payload) => {
          // Fetch the complete record with employee info
          const { data } = await supabase
            .from("feedback")
            .select(`
              *,
              employees(name)
            `)
            .eq("id", payload.new.id)
            .single();
          
          if (data && onInsert) {
            onInsert(data);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "feedback" },
        async (payload) => {
          // Fetch the complete record with employee info
          const { data } = await supabase
            .from("feedback")
            .select(`
              *,
              employees(name)
            `)
            .eq("id", payload.new.id)
            .single();
          
          if (data && onUpdate) {
            onUpdate(data);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "feedback" },
        (payload) => {
          if (onDelete) {
            onDelete(payload.old.id);
          }
        }
      )
      .subscribe();

    return subscription;
  }

  /**
   * Unsubscribe from feedback changes
   * @param {Object} subscription - The subscription to cancel
   */
  static unsubscribeFeedback(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
}